import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CommandPalette, type CommandPaletteProps } from './CommandPalette';
import type { Command, CommandSource } from './types';

export interface CommandPaletteContextValue {
  isOpen: boolean;
  open: (initialQuery?: string) => void;
  close: () => void;
  toggle: () => void;
  /** Registers commands for the lifetime of the caller; returns an unregister function. */
  register: (commands: Command[]) => () => void;
  /**
   * Registers result sources (record search, context switches, AI…). The provider usually sits above the router,
   * so features inside the router contribute their sources through this instead of a module-level singleton.
   */
  registerSources: (sources: CommandSource[]) => () => void;
  /** Sets how remembered navigation commands are replayed (normally the router's `navigate`). */
  setNavigator: (navigate: ((href: string) => void) | null) => void;
  commands: Command[];
  sources: CommandSource[];
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export interface CommandPaletteProviderProps extends Omit<CommandPaletteProps, 'open' | 'onOpenChange' | 'commands' | 'initialQuery' | 'onNavigate'> {
  children: ReactNode;
  /** Static commands (dynamic ones use `useCommandPalette().register`). */
  commands?: Command[];
  /** Install the global mod+K shortcut (default true). */
  shortcut?: boolean;
}

export function CommandPaletteProvider({ children, commands: staticCommands, sources: staticSources, shortcut = true, ...paletteProps }: CommandPaletteProviderProps) {
  const [isOpen, setOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState('');
  const [registered, setRegistered] = useState<Map<symbol, Command[]>>(() => new Map());
  const [registeredSources, setRegisteredSources] = useState<Map<symbol, CommandSource[]>>(() => new Map());
  const [navigator, setNavigatorState] = useState<{ fn: (href: string) => void } | null>(null);
  const setNavigator = useCallback((fn: ((href: string) => void) | null) => setNavigatorState(fn ? { fn } : null), []);
  const registerSources = useCallback((list: CommandSource[]) => {
    const token = Symbol('sources');
    setRegisteredSources((prev) => new Map(prev).set(token, list));
    return () => {
      setRegisteredSources((prev) => {
        const next = new Map(prev);
        next.delete(token);
        return next;
      });
    };
  }, []);

  const open = useCallback((q = '') => {
    setInitialQuery(q);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  const register = useCallback((cmds: Command[]) => {
    const token = Symbol('commands');
    setRegistered((prev) => new Map(prev).set(token, cmds));
    return () => {
      setRegistered((prev) => {
        const next = new Map(prev);
        next.delete(token);
        return next;
      });
    };
  }, []);

  const commands = useMemo(() => {
    const seen = new Set<string>();
    const all: Command[] = [];
    for (const list of [staticCommands ?? [], ...registered.values()]) {
      for (const c of list) {
        if (seen.has(c.id)) continue;
        seen.add(c.id);
        all.push(c);
      }
    }
    return all;
  }, [staticCommands, registered]);

  const sources = useMemo(() => {
    const seen = new Set<string>();
    const all: CommandSource[] = [];
    for (const list of [staticSources ?? [], ...registeredSources.values()]) {
      for (const source of list) {
        if (seen.has(source.id)) continue;
        seen.add(source.id);
        all.push(source);
      }
    }
    return all;
  }, [staticSources, registeredSources]);

  const value = useMemo(
    () => ({ isOpen, open, close, toggle, register, registerSources, setNavigator, commands, sources }),
    [isOpen, open, close, toggle, register, registerSources, setNavigator, commands, sources],
  );

  useCommandPaletteShortcut(shortcut ? toggle : undefined);

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandPalette open={isOpen} onOpenChange={setOpen} commands={commands} sources={sources} onNavigate={navigator?.fn} initialQuery={initialQuery} {...paletteProps} />
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) throw new Error('useCommandPalette must be used within <CommandPaletteProvider>');
  return ctx;
}

/** Registers commands while the calling component is mounted. */
export function useRegisterCommands(commands: Command[]): void {
  const { register } = useCommandPalette();
  useEffect(() => register(commands), [register, commands]);
}

/** Registers result sources while the calling component is mounted (pass a memoized array). */
export function useRegisterCommandSources(sources: CommandSource[]): void {
  const { registerSources } = useCommandPalette();
  useEffect(() => registerSources(sources), [registerSources, sources]);
}

export interface CommandPaletteShortcutOptions {
  /** Key combined with mod (⌘ / Ctrl); default "k". */
  key?: string;
  enabled?: boolean;
}

/** Global mod+K listener. Pass the handler to run (usually `toggle` from useCommandPalette). */
export function useCommandPaletteShortcut(handler: (() => void) | undefined, { key = 'k', enabled = true }: CommandPaletteShortcutOptions = {}): void {
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === key.toLowerCase()) {
        if (!latest.current) return;
        e.preventDefault();
        latest.current();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [key, enabled]);
}
